import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { InferGetStaticPropsType, GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'react-i18next';
import { useTranslation as useNextTranslation } from 'next-i18next';
import Head from 'next/head';
import Header from '../../header';
import Footer from '../../footer';
import styles from '@/styles/Documents.module.css'
import Search from '../../images/search';
import { Swiper, SwiperSlide } from 'swiper/react';
import "swiper/css";
import { Navigation } from 'swiper';
import Link from 'next/link';
import RightArrow from '../../images/rightArrow';
import Support from '../../images/support';
import RefreshArrow from '../../images/refreshArrow';
import BackupClock from '../../images/backupClock';
import Image, { ImageLoaderProps } from 'next/image';
import { Document, documents, progressIconMap } from '../../api/documents';
import LoadingSpinner from '@/components/loading-spinner/loadingSpinner';
import { useCaseIconMap, useCases } from '../../api/useCases';
import { IMAGE_DELIVERY, imageLoader } from "@/base/utils/dataHelper";
import { useRouter } from 'next/router';
import Loading from '@/components/loading/loading';


export const markDocumentAsViewed = (documentId: number) => {
  if (typeof window !== "undefined") {
    let viewedDocuments = JSON.parse(localStorage.getItem('workManagementViewedDocuments') || '[]');
    if (!viewedDocuments.includes(documentId)) {
      viewedDocuments.push(documentId);
      localStorage.setItem('workManagementViewedDocuments', JSON.stringify(viewedDocuments));
    }
  }
};

const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

interface SearchResult {
  id: number
  title: string
  description: string
  link?: string
}

const Documents = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const searchMenuRef = useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation('documentsTranslate');
  const { t: tCommon } = useNextTranslation('common');
  const router = useRouter();
  const [translatedData, setTranslatedData] = useState<Document[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [useCaseTab, setUseCaseTab] = useState(useCases.support)
  const [isSearchInputActive, setIsSearchInputActive] = useState(false)
  const [activeSolutionIndex, setActiveSolutionIndex] = useState<number>(0)
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loadingImage, setLoadingImage] = useState<boolean>(true);

  const isLanguage = router.locale?.toUpperCase().toString()

  useEffect(() => {
    const loadTranslations = async () => {
      await i18n.loadNamespaces('documentsTranslate')

      setTranslatedData(documents)
      setIsLoading(false)
    }

    loadTranslations()
  }, [t, i18n])

  useEffect(() => {
    const handler = (e: any) => {
      if (searchMenuRef.current && !searchMenuRef.current.contains(e.target)) {
        setIsSearchInputActive(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, []);

  const handleActiveSolutionClick = (index: number) => {
    setActiveSolutionIndex(index)
    setLoadingImage(true)
  }

  const combinedData = useMemo(() => [...documents], []);

  const performSearch = useCallback((term: string) => {
    if (term && term.trim() !== '') {
      const searchTermLower = term.toLowerCase();
      const filteredResults = combinedData.flatMap(item => {
        if ('title' in item && typeof item.title === 'string') {
          const title = item.title;
          const description = Array.isArray(item.description)
            ? t(item.description)
            : (typeof item.description === 'string' ? t(item.description) : '');

          if (title.toLowerCase().includes(searchTermLower) || description.toLowerCase().includes(searchTermLower)) {
            return [{
              id: item.initialIndex,
              title: title,
              description: description,
              link: item.link
            }];
          }
        }
        return [];
      });
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  }, [combinedData, t]);

  const debouncedSearch = useCallback((term: string) => {
    const debouncedFunc = debounce((term: string) => performSearch(term), 300);
    debouncedFunc(term);
  }, [performSearch]);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const highlightTerm = (text: string, term: string) => {
    if (!term) return text;

    const regex = new RegExp(`(${term})`, 'gi');
    const parts = text?.split(regex);

    return parts?.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#00323d', color: "#fff", borderRadius: "5px", padding: "0 1px" }}>{part}</span>
      ) : (
        part
      )
    );
  };


  if (isLoading) {
    return <LoadingSpinner />
  }

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = '/work-management/documents';

  return (
    <>
      <Head>
        <title>{tCommon('seo_work_management_documents_title')}</title>
        <meta name="description" content={tCommon('seo_work_management_documents_description')} />
        <meta name="keywords" content={locale === 'tr'
          ? "dokümantasyon, kullanım kılavuzu, teknik dokümantasyon, Stoneity, work management documentation"
          : "documentation, user guide, technical documentation, Stoneity, work management documentation"} />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={tCommon('seo_work_management_documents_title')} />
        <meta property="og:description" content={tCommon('seo_work_management_documents_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={tCommon('seo_work_management_documents_title')} />
        <meta name="twitter:description" content={tCommon('seo_work_management_documents_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <div className={styles.mainFlex}>
        <div className={styles.pageFlex} style={{ paddingTop: "70px" }}>
          <div className={styles.documentsWrapper}>
            <div className={styles.documentsTop}>
              <div className={styles.title}>{t("documentsTopTitle")}</div>
              <div className={styles.searchInputWrapper}
                ref={searchMenuRef}
                onClick={() => setIsSearchInputActive(!isSearchInputActive)}>
                <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} type="text" placeholder={t("search")} className={`${styles.searchInput} ${isSearchInputActive ? styles.searchInputActive : ''}`} />
                <div className={styles.searchIconWrapper}>
                  <Search />
                </div>
                <div className={`${styles.searchInputMenu} ${isSearchInputActive ? styles.searchInputMenuActive : ''}`}>
                  <div className={styles.searchInputMenuTitleWrapper}>
                    <Search color={"#605C5C"} />
                    {t("documentSearchBox")}
                  </div>
                  <div className={styles.searchInputColumnWrapper}>
                    {
                      results.length > 0 ? (<div className={styles.searchResultCardWrapper}>
                        {results.map((result, index) => {
                          return (
                            <Link key={index} className={styles.searchInputColumn} href={`${result.link ? `/work-management/documents${result.link}` : ""}`}>
                              <div className={styles.searchInputColumnLeft}>Doc {(result.id + 1)}</div>
                              <div className={styles.searchInputColumnRight}>{highlightTerm(t(result.description), t(searchTerm))}</div>
                            </Link>
                          )
                        })}
                      </div>) : (<p style={{ marginLeft: "15px" }}>{t("noResultFound")}</p>)
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.stepWrapper}>
            {translatedData.map((document, index) => {
              const viewedDocuments = JSON.parse(localStorage.getItem('workManagementViewedDocuments') || '[]');
              const isViewed = viewedDocuments.includes(document.initialIndex);
              const IconComponent = progressIconMap[document.icon as keyof typeof progressIconMap]
              return (
                <Link href={`/work-management/documents${document.link}`} key={index} className={`${styles.stepperItem}`} onClick={() => markDocumentAsViewed(document.initialIndex)}>
                  <div className={`${styles.stepIconWrapper} ${isViewed ? styles.completed : ""}`}>{IconComponent && <IconComponent color={isViewed ? "#fff" : "#03555E"} />}</div>
                  <div className={styles.stepName}>{t(document.title)}</div>
                  <div className={styles.stepDoc}>(Doc {index + 1})</div>
                </Link>
              )
            }
            )}
          </div>
          <div className={styles.documentCardSwiperWrapper}>
            <Swiper
              breakpoints={{
                0: { slidesPerView: 1 },
                900: { slidesPerView: 2 },
                1230: { slidesPerView: 3 },
              }}
              slidesPerView={3}
              spaceBetween={30}
              className={styles.documentCardMySwiper}
              modules={[Navigation]}
              navigation={{
                prevEl: "." + styles.productFeaturesSlideWrapper,
                nextEl: "." + styles.productFeaturesSlideWrapper2,
              }}
            >
              {translatedData.map((document, i) => {
                const IconComponent = progressIconMap[document.icon as keyof typeof progressIconMap]
                return (
                  <SwiperSlide style={{ height: 'auto' }} key={i} className={styles.documentCard}>
                    <div className={styles.documentCardTop}>
                      {IconComponent && <IconComponent color={"#000000"} />}
                      Doc {i + 1}
                    </div>
                    <div className={styles.documentCardMiddle}>
                      <div className={styles.documentCardMiddleTitle}>{t(document.title)}</div>
                      <div className={styles.documentCardMiddleText}>{t(document.description)}</div>
                    </div>
                    <Link className={styles.documentCardLink} href={`/work-management/documents${document.link}`}>
                      {t("viewAll")}
                      <RightArrow />
                    </Link>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            <div className={styles.productFeaturesSlideWrapper}>
              <div className={styles.productFeaturesCustomLeftArrow}></div>
            </div>
            <div className={styles.productFeaturesSlideWrapper2}>
              <div className={styles.productFeaturesCustomRightArrow}></div>
            </div>
          </div>
        </div >
        <div className={styles.pageFlex}>
          <div className={styles.useCaseWrapper}>
            <div className={styles.useCaseTop}>
              <div className={styles.title}>{t("useCases")}</div>
              <div className={styles.useCaseTabWrapper}>
                <div className={`${styles.useCaseTab} ${useCaseTab === useCases.support ? styles.useCaseTabActive : ''}`} onClick={() => { setUseCaseTab(useCases.support), setActiveSolutionIndex(0) }}>
                  <Support color="#00323D" />
                  {t(useCases.support.title)}
                </div>
                <div className={`${styles.useCaseTab} ${useCaseTab === useCases.development ? styles.useCaseTabActive : ''}`} onClick={() => { setUseCaseTab(useCases.development), setActiveSolutionIndex(0) }}>
                  <RefreshArrow />
                  {t(useCases.development.title)}
                </div>
                <div className={`${styles.useCaseTab} ${useCaseTab === useCases.processes ? styles.useCaseTabActive : ''}`} onClick={() => { setUseCaseTab(useCases.processes), setActiveSolutionIndex(0) }}>
                  <BackupClock />
                  {t(useCases.processes.title)}
                </div>
              </div>
            </div>
            <div className={styles.useCaseContent}>
              <div className={styles.useCaseColumn}>
                <div className={styles.useCaseColumnTitleWrapper}>
                  <div className={styles.useCaseColumnTitle}>{t("useCaseScenario")}</div>
                </div>
                <div className={styles.useCaseColumnTextWrapper}>
                  <div className={styles.useCaseColumnTextTitle}>{t("useCaseCompany")}:</div>
                  <div className={styles.useCaseColumnTextExplain}>{t(useCaseTab.companyAbout)}</div>
                </div>
                <div className={styles.useCaseColumnTextWrapper}>
                  <div className={styles.useCaseColumnTextTitle}>{t("useCaseProblem")}:</div>
                  <div className={styles.useCaseColumnTextExplain}>{t(useCaseTab.companyProblem)}</div>
                </div>
              </div>
              <div className={styles.useCaseColumn}>
                <div className={styles.useCaseColumnTitleWrapper}>
                  <div className={styles.useCaseColumnTitle}>{t("useCaseSolution")}</div>
                  <div className={styles.useCaseColumnTextExplain}>{t(useCaseTab.solution)}</div>
                </div>
                <div className={styles.useCaseMiddleContentWrapper}>
                  <div className={styles.productUseCaseContentWrapper}>
                    <div className={styles.productUseCaseTabWrapper}>
                      <div className={styles.productUseCaseTabs}>
                        {useCaseTab.solutions.map((useCaseSolution, index) => {
                          return (
                            <div key={index} className={`${styles.productUseCaseTab} ${activeSolutionIndex === index && styles.productUseCaseTabActive}`} onClick={() => handleActiveSolutionClick(index)}>{t(useCaseSolution.title)}</div>
                          )
                        })}
                      </div>
                      <div className={styles.productUseCaseContent}>
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          {
                            loadingImage && <Loading />
                          }
                          <Image
                            className={styles.productUseCaseContentImg}
                            src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/${useCaseTab.solutions[activeSolutionIndex]?.imgTR}` : `${IMAGE_DELIVERY}/${useCaseTab.solutions[activeSolutionIndex]?.imgEN}`}
                            alt=""
                            width={680}
                            height={450}
                            onLoadingComplete={() => setLoadingImage(false)}
                            loader={(p: ImageLoaderProps) => imageLoader(p.src, 680, 450)}
                          />
                        </div>
                        <div className={`${styles.productUseCaseContentLeft} ${styles.useCaseColumnTextExplain}`}>
                          {t(useCaseTab.solutions[activeSolutionIndex]?.description)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.responsiveProductUseCaseSwiperWrapper}>
                    <div className={styles.productUseCaseSwiperWrapper}>
                      <Swiper
                        slidesPerView={1}
                        className={styles.productUseCaseMySwiper}
                        modules={[Navigation]}
                        navigation={{
                          prevEl: "." + styles.responsiveProductUseCaseSlideWrapper,
                          nextEl: "." + styles.responsiveProductUseCaseSlideWrapper2,
                        }}
                      >
                        {useCaseTab.solutions.map((useCaseSolution, index) => {
                          return (
                            <SwiperSlide key={index}>
                              <div className={styles.responsiveproductUseCaseTabActiveFlex}>
                                <div className={styles.productUseCaseTabActive}>{t(useCaseSolution.title)}</div>
                              </div>
                              <div className={styles.productUseCaseContent}>
                                <Image
                                  className={styles.productUseCaseContentImg}
                                  src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/${useCaseTab.solutions[index]?.imgTR}` : `${IMAGE_DELIVERY}/${useCaseTab.solutions[index]?.imgEN}`}
                                  alt=""
                                  loader={(p: ImageLoaderProps) => imageLoader(p.src, 680, 450)}
                                  width={680}
                                  height={450}
                                />
                                <div className={`${styles.productUseCaseContentLeft} ${styles.useCaseColumnTextExplain}`}>
                                  {t(useCaseSolution.description)}
                                </div>
                              </div>
                            </SwiperSlide>
                          )
                        })}
                      </Swiper>
                      <div className={styles.responsiveProductUseCaseSlideWrapper}>
                        <div className={styles.responsiveProductUseCaseCustomLeftArrow}></div>
                      </div>
                      <div className={styles.responsiveProductUseCaseSlideWrapper2}>
                        <div className={styles.responsiveProductUseCaseCustomRightArrow}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.useCaseRow}>
                <div className={styles.useCaseRowLeft}>
                  <div className={styles.useCaseRowTitle}>{t("useCaseResult")}</div>
                  <div className={styles.useCaseRowTextExplain}>{t(useCaseTab.result)}</div>
                  <div className={styles.useCaseResponsiveRowRightTitleFlex}>
                    {
                      useCaseTab.resultPercentages.map((percentages, index) => {
                        const IconComponent = useCaseIconMap[percentages.icon as keyof typeof useCaseIconMap]
                        return (
                          <div key={index} className={`${styles.useCaseRowRightTitleWrapper} ${styles.useCaseResponsiveRowRightTitleWrapper}`}>
                            <div className={styles.useCaseRowRightTitle}>{percentages.percent}</div>
                            {IconComponent && <IconComponent width={20} height={20} color={"#DFB866"} />}
                            <div className={styles.useCaseRowRightExplain}>{t(percentages.description)}</div>
                          </div>
                        )
                      })
                    }
                  </div>
                </div>
                <div className={styles.useCaseRowRight}>
                  {
                    useCaseTab.resultPercentages.map((percentages, index) => {
                      const IconComponent = useCaseIconMap[percentages.icon as keyof typeof useCaseIconMap]
                      return (
                        <div key={index} className={styles.useCaseRowRightTitleWrapper}>
                          <div className={styles.useCaseRowRightTitle}>{percentages.percent}</div>
                          {IconComponent && <IconComponent width={20} height={20} color={"#DFB866"} />}
                          <div className={styles.useCaseRowRightExplain}>{t(percentages.description)}</div>
                        </div>
                      )
                    })
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
      <Footer />
    </>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || 'en', ['documentsTranslate', 'common'])),
  },
});

export default Documents;
